// src/Pages/FirstChangePassword.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { setStoredAuth } from "../auth/useAuth";

// ✅ Normalize API base (same rule as everywhere else)
const RAW_API_BASE =
  (typeof import.meta !== "undefined" ? import.meta.env?.VITE_API_BASE : undefined) ||
  process.env.REACT_APP_API_BASE ||
  "https://pos-backend-58o9.onrender.com";

const API_BASE = RAW_API_BASE.replace(/\/+$/, "").replace(/\/api$/i, "");

// ✅ Helper to safely prefix `/api`
const apiPath = (path) =>
  `${API_BASE}${path.startsWith("/api") ? path : `/api${path}`}`;

export default function FirstChangePassword() {
  const nav = useNavigate();
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const email = sessionStorage.getItem("reset_user_email") || "";

  // ✅ If no token, go back to login
  useEffect(() => {
    if (!sessionStorage.getItem("reset_token")) {
      nav("/login", { replace: true });
    }
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");

    if (p1 !== p2) return setErr("Passwords do not match.");
    if (p1.length < 8) return setErr("Password must be at least 8 characters.");

    const resetToken = sessionStorage.getItem("reset_token");
    if (!resetToken) return setErr("Session expired. Please login again.");

    setLoading(true);
    try {
      const res = await fetch(apiPath("/users/first-change"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${resetToken}`,
        },
        body: JSON.stringify({ newPassword: p1 }),
      });

      const body = await res.json().catch(() => null);
      if (!res.ok)
        throw new Error(
          body?.message || body?.error || "Could not update password."
        );

      // ✅ Clear session-only reset data
      sessionStorage.removeItem("reset_token");
      sessionStorage.removeItem("reset_user_email");

      // ✅ Ensure `must_change_password` is cleared locally
      const updatedUser = { ...(body.user || {}), must_change_password: false };

      // ✅ Store token & user persistently
      setStoredAuth(
        { access_token: body.token, user: updatedUser },
        true // remember user
      );

      // ✅ Redirect to dashboard — not back to login
      nav("/", { replace: true });
    } catch (e) {
      console.error("[FirstChangePassword ERROR]", e);
      setErr(e.message || "Could not update password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.wrap}>
      <form onSubmit={submit} style={styles.card}>
        <h2 style={{ margin: 0, marginBottom: 12 }}>Set a New Password</h2>
        {email && (
          <p style={{ marginTop: 0, color: "#546" }}>
            Account: <strong>{email}</strong>
          </p>
        )}
        {err && <div style={styles.err}>{err}</div>}

        <label style={styles.lbl}>
          <span>New password</span>
          <input
            type="password"
            value={p1}
            onChange={(e) => setP1(e.target.value)}
            style={styles.input}
            autoFocus
          />
        </label>

        <label style={styles.lbl}>
          <span>Confirm new password</span>
          <input
            type="password"
            value={p2}
            onChange={(e) => setP2(e.target.value)}
            style={styles.input}
          />
        </label>

        <button type="submit" style={styles.btn} disabled={loading}>
          {loading ? "Updating..." : "Update Password"}
        </button>
      </form>
    </div>
  );
}

const styles = {
  wrap: {
    minHeight: "100vh",
    display: "grid",
    placeItems: "center",
    background: "#f5f7fb",
  },
  card: {
    width: 360,
    background: "#fff",
    padding: 20,
    borderRadius: 12,
    boxShadow: "0 8px 24px rgba(0,0,0,.08)",
    display: "grid",
    gap: 12,
  },
  lbl: { display: "grid", gap: 6, fontSize: 14 },
  input: {
    padding: "10px 12px",
    fontSize: 14,
    border: "1px solid #d9d9d9",
    borderRadius: 8,
    outline: "none",
  },
  btn: {
    marginTop: 8,
    padding: "10px 12px",
    fontWeight: 600,
    borderRadius: 8,
    border: "1px solid #3fa34d",
    background: "#46c75a",
    color: "#fff",
    cursor: "pointer",
  },
  err: {
    background: "#ffecec",
    color: "#b30000",
    padding: "8px 10px",
    borderRadius: 8,
    fontSize: 14,
  },
};
